import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { Switch } from '../components/ui/switch';
import {
  Play, RefreshCw, ChevronDown, ChevronRight, Shield, AlertTriangle,
  CheckCircle2, Clock, MinusCircle, FileText, Users, Activity,
  Eye, EyeOff, Stethoscope, Heart, Zap,
} from 'lucide-react';
import { toast } from 'sonner';

const VISIT_ICONS = {
  'Annual Wellness': <Heart className="w-4 h-4 text-violet-500" />,
  'Follow-up': <Stethoscope className="w-4 h-4 text-blue-500" />,
  'Sick Visit': <Zap className="w-4 h-4 text-amber-500" />,
};

const STATUS_CONFIG = {
  red: { color: 'bg-red-100 text-red-700 border-red-200', label: 'Gaps Found' },
  yellow: { color: 'bg-amber-100 text-amber-700 border-amber-200', label: 'Items Due' },
  green: { color: 'bg-emerald-100 text-emerald-700 border-emerald-200', label: 'All Clear' },
  gray: { color: 'bg-slate-100 text-slate-500 border-slate-200', label: 'Not Evaluated' },
};

const MEASURE_STATUS_ICONS = {
  overdue: <AlertTriangle className="w-3 h-3 text-red-500" />,
  due: <Clock className="w-3 h-3 text-amber-500" />,
  compliant: <CheckCircle2 className="w-3 h-3 text-emerald-500" />,
  excluded: <MinusCircle className="w-3 h-3 text-slate-400" />,
};

function ScoreBreakdown({ debug }) {
  if (!debug?.breakdown) return null;
  const b = debug.breakdown;
  return (
    <div className="mt-1.5 p-2 bg-slate-50 rounded border border-slate-200 text-[10px] space-y-0.5 font-mono" data-testid="score-breakdown">
      <div className="flex justify-between"><span>Clinical</span><span className="text-emerald-600">+{b.clinical_weight?.value}</span></div>
      <div className="flex justify-between"><span>Financial</span><span className="text-blue-600">+{b.financial_weight?.value}</span></div>
      <div className="flex justify-between"><span>Context</span><span className={b.context_weight?.value >= 0 ? 'text-violet-600' : 'text-red-500'}>{b.context_weight?.value >= 0 ? '+' : ''}{b.context_weight?.value}</span></div>
      <div className="flex justify-between"><span>Urgency</span><span className="text-amber-600">+{b.urgency?.value}</span></div>
      <div className="flex justify-between"><span>Suppression</span><span className="text-red-500">-{b.suppression_penalty?.value}</span></div>
      <div className="flex justify-between border-t border-slate-300 pt-0.5 font-bold"><span>Total</span><span>{debug.total_score}</span></div>
      {b.context_weight?.reason && <div className="text-muted-foreground italic">{b.context_weight.reason}</div>}
      {(b.suppression_penalty?.reasons || []).length > 0 && (
        <div className="text-red-400 italic">Penalties: {b.suppression_penalty.reasons.join(', ')}</div>
      )}
    </div>
  );
}

function PatientCard({ patient, debugMode, onStartEncounter, onRefresh }) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [starting, setStarting] = useState(false);
  const header = patient.quality_header || {};
  const headerCfg = STATUS_CONFIG[header.status] || STATUS_CONFIG.gray;
  const ctx = patient.ranking_config?.context || 'unknown';

  const handleStart = async () => {
    setStarting(true);
    try {
      const res = await api.post(`/qil-test/start-encounter/${patient.patient_id}`);
      toast.success(`Encounter started for ${patient.name}`);
      navigate(`/visit/${res.data.encounter_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start encounter');
    } finally {
      setStarting(false);
    }
  };

  return (
    <Card className="border hover:shadow-sm transition-shadow" data-testid={`test-patient-${patient.patient_id}`}>
      <Collapsible open={expanded} onOpenChange={setExpanded}>
        <CardHeader className="py-2.5 px-4">
          <div className="flex items-center gap-3">
            {VISIT_ICONS[patient.visit_type] || <Users className="w-4 h-4 text-slate-400" />}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold">{patient.name}</span>
                <Badge variant="outline" className="text-[10px] font-mono">{patient.mrn}</Badge>
                <span className="text-[10px] text-muted-foreground">{patient.age}{patient.sex === 'M' ? 'M' : 'F'}</span>
              </div>
              <div className="flex items-center gap-2 mt-0.5">
                <Badge variant="outline" className="text-[9px]">{patient.visit_type}</Badge>
                <span className={`text-[9px] px-1.5 py-0.5 rounded border font-medium ${headerCfg.color}`}>
                  {headerCfg.label}
                </span>
                {debugMode && <span className="text-[9px] text-violet-500 font-mono">ctx={ctx}</span>}
                {patient.problems?.length > 0 && (
                  <span className="text-[9px] text-muted-foreground truncate max-w-48">{patient.problems.join(', ')}</span>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              {/* Quality summary badges */}
              <div className="flex gap-1">
                {header.must_address_count > 0 && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-50 text-red-600 font-medium">
                    {header.must_address_count} must
                  </span>
                )}
                {header.optional_count > 0 && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-600 font-medium">
                    {header.optional_count} opt
                  </span>
                )}
              </div>

              {patient.latest_encounter_id ? (
                <Button size="sm" variant="outline" className="h-7 text-xs gap-1"
                  onClick={() => navigate(`/visit/${patient.latest_encounter_id}`)}
                  data-testid={`resume-encounter-${patient.patient_id}`}
                >
                  <FileText className="w-3 h-3" /> Resume
                </Button>
              ) : (
                <Button size="sm" className="h-7 text-xs gap-1"
                  onClick={handleStart} disabled={starting}
                  data-testid={`start-encounter-${patient.patient_id}`}
                >
                  <Play className="w-3 h-3" /> {starting ? 'Starting...' : 'Start Visit'}
                </Button>
              )}

              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" data-testid={`expand-${patient.patient_id}`}>
                  {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </Button>
              </CollapsibleTrigger>
            </div>
          </div>
        </CardHeader>

        <CollapsibleContent>
          <CardContent className="pt-0 pb-3 px-4 space-y-3">
            {/* Must Address Cards */}
            {patient.must_address?.length > 0 && (
              <div>
                <h4 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
                  Must Address ({patient.must_address.length})
                  {debugMode && patient.ranking_config && (
                    <span className="ml-2 text-violet-500 normal-case font-mono">
                      max={patient.ranking_config.max_cards} | eval={patient.ranking_config.total_evaluated}
                    </span>
                  )}
                </h4>
                <div className="space-y-1">
                  {patient.must_address.map(g => (
                    <div key={g.measure_id} className="p-2 rounded border bg-white">
                      <div className="flex items-center gap-2">
                        {MEASURE_STATUS_ICONS[g.status]}
                        <span className="text-xs font-medium flex-1">{g.name}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                          g.priority === 'high' ? 'bg-red-50 text-red-600' :
                          g.priority === 'medium' ? 'bg-amber-50 text-amber-600' : 'bg-slate-50 text-slate-500'
                        }`}>{g.priority}</span>
                        {debugMode && g.debug_score && (
                          <span className="text-[10px] font-mono text-violet-500">
                            score={g.debug_score.total_score}
                          </span>
                        )}
                      </div>
                      {debugMode && g.debug_score && <ScoreBreakdown debug={g.debug_score} />}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Optional */}
            {patient.optional?.length > 0 && (
              <div>
                <h4 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Optional ({patient.optional.length})</h4>
                <div className="flex flex-wrap gap-1">
                  {patient.optional.map(g => (
                    <span key={g.measure_id} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-50 border text-slate-600">
                      {g.name} ({g.status})
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Debug: Suppression Log */}
            {debugMode && patient.suppression_log?.length > 0 && (
              <div>
                <h4 className="text-[10px] font-semibold uppercase tracking-wider text-violet-500 mb-1">Suppression Log (top 10)</h4>
                <div className="p-2 bg-violet-50/50 rounded border border-violet-100 space-y-0.5">
                  {patient.suppression_log.map((s, i) => (
                    <div key={i} className="text-[10px] font-mono text-violet-600">
                      <span className="text-violet-400">{s.measure_id?.replace('qm_', '')}</span>
                      {' → '}{s.reason}
                      {s.status && <span className="text-muted-foreground"> (status={s.status})</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Summary stats */}
            <div className="flex gap-3 text-[10px] text-muted-foreground">
              <span>Deferred: {patient.deferred_count}</span>
              <span>Suppressed: {patient.suppressed_count}</span>
              {patient.encounter_count > 0 && <span>Encounters: {patient.encounter_count}</span>}
              {patient.has_transcript && <span className="text-emerald-600 font-medium">Transcript ready</span>}
            </div>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}

export default function QilTestDashboard() {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [debugMode, setDebugMode] = useState(true);
  const [seeding, setSeeding] = useState(false);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/qil-test/patient-summary');
      setPatients(res.data.patients || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const handleSeed = async () => {
    setSeeding(true);
    try {
      const res = await api.post('/qil-test/seed-patients');
      toast.success(`Seeded ${res.data.count} patients`);
      fetchSummary();
    } catch (err) {
      toast.error('Failed to seed patients');
    } finally { setSeeding(false); }
  };

  const seededCount = patients.filter(p => p.patient_id).length;
  const awvPatients = patients.filter(p => p.visit_type === 'Annual Wellness');
  const chronicPatients = patients.filter(p => p.visit_type === 'Follow-up');
  const acutePatients = patients.filter(p => p.visit_type === 'Sick Visit');

  return (
    <div className="p-4 max-w-5xl mx-auto space-y-4" data-testid="qil-test-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2">
            <Activity className="w-5 h-5 text-primary" />
            QIL Test Dashboard
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            ACO/MIPS Quality Testing — {seededCount}/7 patients seeded
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Switch
              checked={debugMode}
              onCheckedChange={setDebugMode}
              data-testid="debug-mode-toggle"
            />
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              {debugMode ? <Eye className="w-3.5 h-3.5 text-violet-500" /> : <EyeOff className="w-3.5 h-3.5" />}
              {debugMode ? 'Debug On' : 'Debug Off'}
            </span>
          </div>
          <Button variant="outline" size="sm" onClick={fetchSummary} data-testid="dashboard-refresh">
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
          </Button>
          {seededCount < 7 && (
            <Button size="sm" onClick={handleSeed} disabled={seeding} data-testid="seed-patients-btn">
              <Users className="w-3.5 h-3.5 mr-1" /> {seeding ? 'Seeding...' : 'Seed Patients'}
            </Button>
          )}
        </div>
      </div>

      {/* Scoring Formula Reference */}
      {debugMode && (
        <Card className="border border-violet-200 bg-violet-50/30" data-testid="scoring-formula">
          <CardContent className="p-3">
            <div className="flex items-start gap-2">
              <Shield className="w-4 h-4 text-violet-500 mt-0.5 shrink-0" />
              <div className="text-[11px] space-y-1">
                <div className="font-semibold text-violet-700">Ranking Formula (deterministic, auditable)</div>
                <code className="text-[10px] text-violet-600 bg-violet-100 px-2 py-0.5 rounded block">
                  score = clinical_weight + financial_weight + context_weight + urgency - suppression_penalty
                </code>
                <div className="text-muted-foreground grid grid-cols-2 gap-x-6 gap-y-0.5 mt-1">
                  <span>Clinical: high=100, med=50, low=20</span>
                  <span>Urgency: overdue=40, due=20, unknown=10</span>
                  <span>Context: annual=30-40, match=25, chronic=20</span>
                  <span>Financial: billing_opp+annual=15</span>
                  <span>Suppression: declined=-80, deferred=-50, recent=-30</span>
                  <span>AWV cap=5, Chronic cap=3, Acute cap=2</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="space-y-3">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-20 rounded-lg" />)}
        </div>
      ) : (
        <>
          {/* AWV Section */}
          {awvPatients.length > 0 && (
            <div>
              <h2 className="text-sm font-semibold flex items-center gap-2 mb-2">
                <Heart className="w-4 h-4 text-violet-500" />
                Annual Wellness Visits ({awvPatients.length})
                <span className="text-[10px] font-normal text-muted-foreground">— assertive, guided</span>
              </h2>
              <div className="space-y-2">
                {awvPatients.map(p => (
                  <PatientCard key={p.patient_id || p.name} patient={p} debugMode={debugMode} onStartEncounter={() => {}} onRefresh={fetchSummary} />
                ))}
              </div>
            </div>
          )}

          {/* Chronic Follow-ups */}
          {chronicPatients.length > 0 && (
            <div>
              <h2 className="text-sm font-semibold flex items-center gap-2 mb-2">
                <Stethoscope className="w-4 h-4 text-blue-500" />
                Chronic Follow-ups ({chronicPatients.length})
                <span className="text-[10px] font-normal text-muted-foreground">— moderate, relevant only</span>
              </h2>
              <div className="space-y-2">
                {chronicPatients.map(p => (
                  <PatientCard key={p.patient_id || p.name} patient={p} debugMode={debugMode} onStartEncounter={() => {}} onRefresh={fetchSummary} />
                ))}
              </div>
            </div>
          )}

          {/* Acute */}
          {acutePatients.length > 0 && (
            <div>
              <h2 className="text-sm font-semibold flex items-center gap-2 mb-2">
                <Zap className="w-4 h-4 text-amber-500" />
                Acute / Sick Visits ({acutePatients.length})
                <span className="text-[10px] font-normal text-muted-foreground">— quiet, critical only</span>
              </h2>
              <div className="space-y-2">
                {acutePatients.map(p => (
                  <PatientCard key={p.patient_id || p.name} patient={p} debugMode={debugMode} onStartEncounter={() => {}} onRefresh={fetchSummary} />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
